import {
  useEffect,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react";
import styled, { keyframes } from "styled-components";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import videojs from "video.js";
import { Link } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { useMutation } from "@apollo/client";
import {
  FaArrowRight,
  FaVolumeMute,
  FaVolumeUp,
  FaTimes,
} from "react-icons/fa";

import { ClipOfTheDayViewModel } from "./ViewModel";
import { TopComment } from "./TopComment";
import { IProPlayerInfo, ITag } from "./@types";
import { IFeaturedItem } from "../Featured/@types";
import { FeaturedContainer, CONTENT_MAX_WIDTH } from "../styledComponents";
import { FeaturedUser } from "../FeaturedUser";
import { analyticsClickEvent } from "MVVM/Components/Overlays/utils";
import { OverlayButton } from "MVVM/Components/Overlays";
import { ClipBadges } from "MVVM/Components/ClipBadge";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { ClipStatBar } from "MVVM/Components/ClipStatBar";
import { LoginUpsell } from "MVVM/Components/LoginUpsell";
import { VideoPlayer } from "MVVM/VideoPlayer";
import { ModalType } from "MVVM/@types";
import { ClipFullModalState } from "MVVM/State/modals";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { MUTATE_REACTION } from "MVVM/GraphQL/addReaction";
import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { analytics } from "MVVM/Analytics";
import background from "MVVM/Assets/Homepage/cs-carousel.webp";
import CotdImage from "../../../../assets/images/feed/COTD_New.svg";

const HERO_STACK_BREAK = "1280px";

const ClipOfTheDay = () => {
  const { clipOfTheDay, proPlayer, tags, loading } = ClipOfTheDayViewModel();

  if (loading)
    return (
      <HeroWrapper style={{ backgroundImage: `url(${background})` }}>
        <FilterWrapper>
          <FeaturedContainer
            justifyContent="center"
            style={{ position: "relative", zIndex: 1 }}
          >
            <SkeletonCard>
              <LoadingSkeleton height="100%" width="100%" />
            </SkeletonCard>
          </FeaturedContainer>
        </FilterWrapper>
      </HeroWrapper>
    );

  if (!clipOfTheDay) return <></>;

  return (
    <HeroWrapper style={{ backgroundImage: `url(${background})` }}>
      <FilterWrapper>
        <FeaturedContainer
          justifyContent="center"
          style={{ position: "relative", zIndex: 1 }}
        >
          <HeroRow>
            <AllstarAnnouncementCard />
            <ClipColumn>
              <ClipOfTheDayHero
                item={clipOfTheDay}
                proPlayer={proPlayer}
                tags={tags}
              />
            </ClipColumn>
          </HeroRow>
        </FeaturedContainer>
      </FilterWrapper>
    </HeroWrapper>
  );
};

const ALLSTAR_BANNER_DISMISSED_KEY = "allstarBannerDismissed";

const AllstarAnnouncementCard = () => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(ALLSTAR_BANNER_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const dismiss = (event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      localStorage.setItem(ALLSTAR_BANNER_DISMISSED_KEY, "1");
    } catch {
      /* storage unavailable */
    }
    setDismissed(true);
    analytics.track("Homepage Announcement - Dismiss", {
      promotionName: "!allstar",
    });
  };

  return (
    <AnnounceCard
      to="/!allstar"
      onClick={() =>
        analytics.track("Homepage Announcement - Click", {
          promotionName: "!allstar",
        })
      }
    >
      <DismissButton
        className="announce-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
      >
        <FaTimes size={13} />
      </DismissButton>
      <AnnounceEyebrow>
        <AnnounceDate>June 29, 2026</AnnounceDate>
        <AnnounceTag>New</AnnounceTag>
      </AnnounceEyebrow>
      <AnnounceContent>
        <AnnounceTitle>
          Skip clip limits by typing <Cmd>!allstar</Cmd> in-game for free clips
        </AnnounceTitle>
        <AnnounceBottomRow>
          <AnnounceBody>
            Just type <Cmd>!allstar</Cmd> in-game (in all chat) after you do
            something you want to clip. If we can access your match, we'll make
            the clip for free, even if you've hit your clip limit.
          </AnnounceBody>
          <AnnounceCta className="announce-cta">
            Read more <FaArrowRight size={12} />
          </AnnounceCta>
        </AnnounceBottomRow>
      </AnnounceContent>
    </AnnounceCard>
  );
};

const ClipOfTheDayHero = ({
  item,
  proPlayer,
  tags,
}: {
  item: IFeaturedItem;
  proPlayer: IProPlayerInfo | null;
  tags: ITag[];
}) => {
  const videoPlayerRef = useRef<videojs.Player | null>(null);
  const setClipFullModalState = useSetRecoilState(ClipFullModalState);
  const [muted, setMuted] = useState(true);
  const [likeCount, setLikeCount] = useState(item.likeCount ?? 0);
  const [hasLiked, setHasLiked] = useState(false);

  const { allstarUser } = useUser();
  const [mutateReaction] = useMutation(MUTATE_REACTION, {
    ignoreResults: true,
  });
  const { portalOpen, setPortalOpen, Portal } = usePortal(
    <LoginUpsell ctaText="Like" />,
  );

  useEffect(() => {
    analytics.track("Clip of the Day - Impression", {
      type: "homepageHero",
      shareId: item.shareId,
      promotionName: item.title,
    });
  }, [item.shareId, item.title]);

  const analyticsContext = {
    shareId: item.shareId,
    player: proPlayer?.playerName,
    team: proPlayer?.teamName,
    event: proPlayer?.eventName,
  };

  const submitLike = () => {
    analytics.track("Button - Click", {
      locationIdentifier: "react",
      location: "clip_of_the_day",
    });
    if (!allstarUser.loggedIn) return setPortalOpen(true);
    // ponytail: heart maps to the app's default FIRE reaction; no unlike endpoint
    mutateReaction({
      variables: {
        input: { clipId: item.internalId || item.shareId, reaction: "FIRE" },
      },
    });
    setLikeCount((count) => count + 1);
    setHasLiked(true);
  };

  const openModal = (tab?: string) => {
    if (!item.shareId) return;
    analyticsClickEvent(item.type);
    analytics.track("Clip of the Day - Watch", analyticsContext);
    // mute the hero so it doesn't fight the modal clip's audio
    videoPlayerRef.current?.muted(true);
    setMuted(true);
    setClipFullModalState({
      isOpen: ModalType.Clip,
      shareId: item.shareId,
      orientation: "LANDSCAPE",
      contextOverride: {
        type: "default",
        metadata: { feedTitle: "Clip of the Day", source: "modal", tab },
      },
    });
  };

  // Bare click handlers so the DOM event is never forwarded as the tab arg.
  const openClip = () => openModal();
  const openComments = () => openModal("comments");

  const toggleMute = (event: ReactMouseEvent) => {
    event.stopPropagation();
    const next = !muted;
    videoPlayerRef.current?.muted(next);
    setMuted(next);
    analytics.track(
      next ? "Clip of the Day - Mute" : "Clip of the Day - Unmute",
      analyticsContext,
    );
  };

  return (
    <>
      {portalOpen && Portal}
      <HeroCard className="cotd-hero">
        <VideoPlayerRounder onClick={openClip}>
          <CotdBadge src={CotdImage} aria-hidden />
          <Poster src={item.thumbnailUrl} width="100%" />
          <Box style={{ position: "relative", zIndex: 2, lineHeight: 0 }}>
            <VideoPlayer
              refForVideoPlayer={videoPlayerRef}
              mp4Source={item.url}
              loop={true}
              eventSrc="ClipOfTheDay"
              playAnalyticsProperties={{
                shareId: item.shareId,
                source: "Homepage Clip of the Day",
              }}
              watchAnalyticsProperties={{
                action: "watch",
                collection: "Homepage Clip of the Day",
                collectionType: "Generic",
                item: item.shareId,
                itemType: "Clip",
              }}
              options={{
                aspectRatio: "16:9",
                autoplay: "muted",
                controls: false,
                fill: true,
                fluid: true,
                muted: true,
                playsinline: true,
                poster: item.thumbnailUrl,
                responsive: true,
                userActions: {
                  click: false,
                },
              }}
            />
          </Box>
          <MuteToggle
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <FaVolumeMute size={15} /> : <FaVolumeUp size={15} />}
          </MuteToggle>
          <VideoBottomRight>
            {item.id && <TopComment clipId={item.id} onOpen={openComments} />}
            <ClipStatBar
              views={item.views}
              likeCount={likeCount}
              commentCount={item.commentCount}
              isLiked={hasLiked}
              onView={openClip}
              onLike={submitLike}
              onComment={openComments}
            />
          </VideoBottomRight>
        </VideoPlayerRounder>
        {proPlayer ? (
          <ProPlayerPanel
            item={item}
            proPlayer={proPlayer}
            tags={tags}
            onOpenClip={openClip}
          />
        ) : (
          <DefaultPanel item={item} tags={tags} />
        )}
      </HeroCard>
    </>
  );
};

const GameplayBadges = ({ tags }: { tags: ITag[] }) => {
  if (!tags.length) return null;
  return (
    <BadgeRow>
      <ClipBadges tags={tags} size="md" maxVisible={3} expandable />
    </BadgeRow>
  );
};

const DefaultPanel = ({
  item,
  tags,
}: {
  item: IFeaturedItem;
  tags: ITag[];
}) => (
  <HeroSection>
    <Flex flexDirection="column">
      <HeroTitle mb={3}>Clip of the Day</HeroTitle>
      <MobileHide>
        <FeaturedUser
          avatar={item.avatar}
          createdAt={item.createdAt}
          userId={item.userId}
          username={item.username}
          views={item.views}
        />
      </MobileHide>
      <GameplayBadges tags={tags} />
    </Flex>
    <OverlayButton
      buttonCopy="Go To Clip of the Day!"
      link={item.link}
      name={item.title}
      type={item.type}
      style={buttonStyle}
    />
  </HeroSection>
);

const ProPlayerPanel = ({
  item,
  proPlayer,
  tags,
  onOpenClip,
}: {
  item: IFeaturedItem;
  proPlayer: IProPlayerInfo;
  tags: ITag[];
  onOpenClip: () => void;
}) => {
  const profileLink = proPlayer.playerGameIdentifier
    ? `/u/${proPlayer.playerGameIdentifier}`
    : undefined;
  const flagUrl = proPlayer.countryISO
    ? `${
        process.env.REACT_APP_S3_CLIP_BUCKET
      }/static/web/flags/${proPlayer.countryISO.toLowerCase()}.png`
    : undefined;
  const clipDate = item.createdAt
    ? dateFormatWithDistanceSuffix(item.createdAt)
    : "";

  return (
    <ProSection>
      {proPlayer.teamLogo && (
        <TeamBackdrop src={proPlayer.teamLogo} aria-hidden />
      )}
      <ProContent>
        {proPlayer.teamName && (
          <Matchup>
            {proPlayer.teamLogo && <MatchupLogo src={proPlayer.teamLogo} />}
            <MatchupTeam title={proPlayer.teamName}>
              {proPlayer.teamName}
            </MatchupTeam>
            {proPlayer.opponentTeamName && (
              <>
                <ScorePill>{proPlayer.scoreLine || "VS"}</ScorePill>
                {proPlayer.opponentTeamLogo && (
                  <MatchupLogo src={proPlayer.opponentTeamLogo} />
                )}
                <MatchupTeam title={proPlayer.opponentTeamName}>
                  {proPlayer.opponentTeamName}
                </MatchupTeam>
              </>
            )}
          </Matchup>
        )}

        {proPlayer.eventName && (
          <EventHeader>
            {proPlayer.eventLogo && (
              <EventHeaderLogo src={proPlayer.eventLogo} />
            )}
            <Flex flexDirection="column" style={{ minWidth: 0 }}>
              <EventHeaderName>{proPlayer.eventName}</EventHeaderName>
              {proPlayer.seriesGameLabel && (
                <EventHeaderSeries>
                  {proPlayer.seriesGameLabel}
                </EventHeaderSeries>
              )}
            </Flex>
          </EventHeader>
        )}

        {item.title && (
          <ClipHeadline onClick={onOpenClip}>{item.title}</ClipHeadline>
        )}

        <Flex style={{ gap: 16, alignItems: "center" }}>
          {proPlayer.avatar &&
            (profileLink ? (
              <Link to={profileLink}>
                <PlayerAvatar src={proPlayer.avatar} />
              </Link>
            ) : (
              <PlayerAvatar src={proPlayer.avatar} />
            ))}
          <Flex flexDirection="column" style={{ minWidth: 0 }}>
            <Flex style={{ gap: 8, alignItems: "center", minWidth: 0 }}>
              {flagUrl && <NameFlag src={flagUrl} aria-hidden />}
              {profileLink ? (
                <PlayerNameLink to={profileLink}>
                  {proPlayer.playerName}
                </PlayerNameLink>
              ) : (
                <PlayerName>{proPlayer.playerName}</PlayerName>
              )}
            </Flex>
            {proPlayer.playerFullName && (
              <PlayerFullName>{proPlayer.playerFullName}</PlayerFullName>
            )}
            {clipDate && <ClipDate>{clipDate}</ClipDate>}
          </Flex>
        </Flex>

        <GameplayBadges tags={tags} />
      </ProContent>
    </ProSection>
  );
};

const buttonStyle = {
  borderRadius: "12px",
  fontWeight: 700,
  lineHeight: "1em",
  position: "static" as const,
  minHeight: "20px",
};

const SkeletonCard = styled(Box)`
  border-radius: 12px;
  height: 440px;
  line-height: 0;
  max-width: 1120px;
  overflow: hidden;
  width: 100%;

  span {
    display: block;
    height: 100%;
  }

  @media (max-width: ${HERO_STACK_BREAK}) {
    height: 78vw;
    max-height: 620px;
  }
`;

const HeroWrapper = styled(Box)`
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  /* bleed the blurred background out to the content-area edges (cancels
     HomepageWrapper's padding) so it stays immersive; HeroRow re-caps the
     banner + clip to line up with the feeds. */
  margin: 0 -60px -16px -60px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    margin: 0 -24px -16px -24px;
  }
`;

const FilterWrapper = styled(Box)`
  backdrop-filter: blur(8px);
  height: 100%;
  margin: 0;
  padding: 44px 0 0;
  position: relative;

  ::before {
    background: linear-gradient(
      0deg,
      rgb(29 42 52) 0%,
      rgb(27 42 52) 0%,
      rgb(0 0 0 / 38%) 50%
    );
    content: "";
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 0;
  }

  @media (max-width: ${HERO_STACK_BREAK}) {
    /* small top gap so the banner clears the navbar instead of sitting flush */
    padding: 20px 0 0;
  }
`;

const HeroCard = styled(Flex)`
  border-radius: 12px;
  height: auto;
  margin: 0 auto;
  max-width: 1120px;
  width: 100%;

  @media (max-width: ${HERO_STACK_BREAK}) {
    flex-direction: column;
    width: 100vw;
  }
`;

/* Announcement banner on top, clip below. The padding re-insets the content
   after HeroWrapper's bleed (matching the wrapper padding the feeds get) so the
   banner + clip line up with the feeds at every width; the blurred background
   bleeds wider behind it. The +120px is the two 60px gutters at the cap. */
const HeroRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0 auto;
  max-width: calc(${CONTENT_MAX_WIDTH} + 120px);
  padding: 0 60px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    padding: 0 24px;
  }
`;

const ClipColumn = styled.div`
  width: 100%;
  min-width: 0;

  /* fill the full content width instead of the clip's own 1120px max */
  .cotd-hero {
    max-width: none;
    margin: 0;
    width: 100%;
  }
`;

const AnnounceCard = styled(Link)`
  background-color: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  box-sizing: border-box;
  display: block;
  overflow: hidden;
  padding: 13px 22px;
  position: relative;
  text-decoration: none;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
  width: 100%;

  ::before {
    background-image: url("${process.env
      .REACT_APP_S3_CLIP_BUCKET}/static/web/in-game/bg.jpg");
    background-position: center;
    background-size: cover;
    content: "";
    inset: 0;
    position: absolute;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  & > .announce-dismiss {
    position: absolute;
    right: 14px;
    top: 12px;
    z-index: 2;
  }

  &:hover {
    box-shadow: 0 16px 40px -22px rgba(0, 0, 0, 0.8);
    transform: translateY(-2px);
  }

  &:hover .announce-cta svg {
    transform: translateX(3px);
  }
`;

const DismissButton = styled.button`
  align-items: center;
  background: rgba(0, 0, 0, 0.35);
  border: none;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  height: 26px;
  justify-content: center;
  transition: background 0.15s ease;
  width: 26px;

  &:hover {
    background: rgba(0, 0, 0, 0.55);
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const AnnounceEyebrow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
`;

const AnnounceDate = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const AnnounceTag = styled.span`
  background: ${({ theme }) => theme.colors.alert};
  border-radius: 100px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 4px 11px;
  text-transform: uppercase;
`;

const AnnounceContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 740px;
  /* shrink to the text's real width so "Read more" sits just past the
     paragraph instead of stranded at a fixed-width right edge */
  width: fit-content;
`;

/* paragraph + "Read more" share a row; the CTA sits at the content's right
   edge (which lines up with the header's right edge) */
const AnnounceBottomRow = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-top: 8px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
`;

const AnnounceTitle = styled.h3`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 0;
`;

const AnnounceBody = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 15px;
  line-height: 1.45;
  margin: 0;
  max-width: 560px;
`;

const Cmd = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  font-weight: 600;
  white-space: nowrap;
`;

const AnnounceCta = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: inline-flex;
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 700;
  gap: 8px;
  white-space: nowrap;

  svg {
    transition: transform 0.15s ease;
  }
`;

const VideoPlayerRounder = styled(Box)`
  cursor: pointer;
  flex: 1;
  font-size: 0;
  line-height: 0;
  min-width: 0;
  overflow: hidden;
  position: relative;

  @media (max-width: ${HERO_STACK_BREAK}) {
    flex: none;
    width: 100%;
  }

  .video-js {
    display: block;
    vertical-align: bottom;
  }

  .video-js,
  .vjs-poster,
  .vjs-tech,
  .vjs-modal-dialog-content,
  .vjs-error-display {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;

    @media (max-width: ${HERO_STACK_BREAK}) {
      border-radius: 0;
    }
  }

  .video-js .vjs-control-bar {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const CotdBadge = styled.img`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 104px;
  z-index: 3;
  pointer-events: none;
`;

const MuteToggle = styled.button`
  align-items: center;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 50%;
  bottom: 14px;
  color: #fff;
  cursor: pointer;
  display: flex;
  height: 34px;
  justify-content: center;
  left: 14px;
  position: absolute;
  width: 34px;
  z-index: 4;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

const Poster = styled(Image)`
  position: absolute;
  z-index: 1;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
`;

const VideoBottomRight = styled.div`
  align-items: flex-end;
  bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 68%;
  position: absolute;
  right: 14px;
  z-index: 4;
`;

const HeroSection = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.darkerNed};
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  flex-direction: column;
  justify-content: space-between;
  padding: 36px;
  width: 380px;

  @media (max-width: ${HERO_STACK_BREAK}) {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    border-top-right-radius: 0;
    padding: 22px;
    width: 100%;
  }
`;

const ProSection = styled(Box)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  overflow: hidden;
  position: relative;
  width: 380px;

  @media (max-width: ${HERO_STACK_BREAK}) {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    border-top-right-radius: 0;
    width: 100%;
  }
`;

const sheen = keyframes`
  0% { transform: translateX(-40%) rotate(8deg); opacity: 0.0; }
  35% { opacity: 0.16; }
  100% { transform: translateX(160%) rotate(8deg); opacity: 0.0; }
`;

const TeamBackdrop = styled.img`
  position: absolute;
  right: -60px;
  top: 50%;
  transform: translateY(-50%);
  width: 460px;
  height: 460px;
  object-fit: contain;
  opacity: 0.26;
  filter: saturate(1.3);
  pointer-events: none;
  z-index: 0;
`;

const NameFlag = styled.img`
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 3px;
  flex-shrink: 0;
`;

const ProContent = styled(Box)`
  position: absolute;
  inset: 0;
  z-index: 1;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  /* Center the content so a tall card frames it top and bottom (over the team
     crest) instead of dumping all the dead space below the badges. */
  justify-content: center;
  overflow: hidden;

  /* Keep every row at its natural height so nothing (title, tags) shrinks and
     clips into its neighbour. The panel stacks below the video before it gets
     too short to fit (see HERO_STACK_BREAK). */
  > * {
    flex-shrink: 0;
  }

  background: linear-gradient(
    115deg,
    rgba(20, 30, 38, 0.35) 0%,
    rgba(20, 30, 38, 0.6) 55%,
    rgba(20, 30, 38, 0.72) 100%
  );

  ::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 60px;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    animation: ${sheen} 6s ease-in-out infinite;
    pointer-events: none;
  }

  @media (max-width: ${HERO_STACK_BREAK}) {
    position: static;
    padding: 22px;
  }
`;

const EventHeader = styled(Flex)`
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const EventHeaderLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;
`;

const EventHeaderName = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: clamp(18px, 1.25vw, 24px);
  font-weight: 800;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EventHeaderSeries = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  margin-top: 2px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PlayerAvatar = styled.img`
  width: clamp(76px, 5vw, 104px);
  height: clamp(76px, 5vw, 104px);
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const PlayerName = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: clamp(28px, 1.9vw, 38px);
  font-weight: 800;
  line-height: 1.05;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlayerNameLink = styled(Link)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: clamp(28px, 1.9vw, 38px);
  font-weight: 800;
  line-height: 1.05;
  overflow: hidden;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const PlayerFullName = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: clamp(15px, 1vw, 18px);
  font-weight: 500;
  margin-top: 2px;
`;

const Matchup = styled(Flex)`
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const MatchupLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
`;

const MatchupTeam = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: clamp(14px, 0.95vw, 17px);
  font-weight: 800;
  letter-spacing: 0.02em;
  max-width: 180px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
`;

const ScorePill = styled(Text)`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 7px;
  color: ${({ theme }) => theme.colors.ice};
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.02em;
  padding: 4px 11px;
`;

const BadgeRow = styled(Box)`
  margin-top: 18px;
`;

const ClipDate = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: clamp(12px, 0.85vw, 14px);
  font-weight: 600;
  margin-top: 4px;
`;

/* The clip's own title, used to fill the pro panel's vertical space and give
   the play a headline. Clamped so a long title can't blow out the card. */
const ClipHeadline = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: clamp(21px, 1.6vw, 30px);
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 16px;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const MobileHide = styled(Box)`
  @media (max-width: ${HERO_STACK_BREAK}) {
    display: none;
  }
`;

const HeroTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 800;
  font-size: 32px;

  @media (max-width: ${HERO_STACK_BREAK}) {
    font-size: ${({ theme }) => theme.fontSizes[3]};
    margin-top: -2px;
    margin-bottom: 20px;
    max-width: 70%;
  }
`;

export { ClipOfTheDay };
